import { ArrowLeftIcon, ArrowRightIcon } from '@motorway/mw-highway-code';

import cypressIds from 'Components/cypress_ids.json';
import { useBreakpoint } from 'Context/BreakpointsContext/BreakpointsContext';

import type { SimilarVehiclesEvents, SimilarVehiclesSliderSettings } from '../SimilarVehicles';

import type { UseSimilarVehiclesCarousel } from './SimilarVehiclesCarousel.types';
import { SimilarVehiclesCarouselArrow } from './SimilarVehiclesCarouselArrow';

const { similarVehiclesCarouselNext, similarVehiclesCarouselPrev } = cypressIds.buttons;

type UseSimilarVehiclesCarouselPayload = {
	childrenCount: number;
	isLoading: boolean;
	onNextArrowClick: SimilarVehiclesEvents['onNextArrowClick'];
	onPrevArrowClick: SimilarVehiclesEvents['onPrevArrowClick'];
	settings?: SimilarVehiclesSliderSettings;
};

export const useSimilarVehiclesCarousel = ({
	childrenCount,
	isLoading,
	onNextArrowClick,
	onPrevArrowClick,
	settings,
}: UseSimilarVehiclesCarouselPayload): UseSimilarVehiclesCarousel => {
	const {
		minWidth: { breakpointTablet: isDesktop },
	} = useBreakpoint();

	const slidesToShow = isDesktop ? 3 : 1;

	const mergedSliderSettings: SimilarVehiclesSliderSettings = {
		arrows: Boolean(isDesktop) && childrenCount > slidesToShow,
		beforeChange(currentSlide: number, nextSlide: number): void {
			if (currentSlide < nextSlide) {
				onNextArrowClick?.();
			}

			if (currentSlide > nextSlide) {
				onPrevArrowClick?.();
			}
		},
		dots: !isDesktop && childrenCount > 1,
		draggable: !isDesktop,
		hideDefaultNextArrow: false,
		hideDefaultPrevArrow: false,
		infinite: false,
		nextArrow: isDesktop ? (
			<SimilarVehiclesCarouselArrow
				containerCypressId={similarVehiclesCarouselNext}
				icon={ArrowRightIcon}
				label="Next"
			/>
		) : undefined,
		prevArrow: isDesktop ? (
			<SimilarVehiclesCarouselArrow
				containerCypressId={similarVehiclesCarouselPrev}
				icon={ArrowLeftIcon}
				label="Prev"
			/>
		) : undefined,
		showOverflow: false,
		slidesToScroll: slidesToShow,
		slidesToShow,
		speed: 500,
		swipeToSlide: !isDesktop,
		...settings,
		...(isLoading
			? {
					arrows: false,
					dots: false,
					draggable: false,
					swipeToSlide: false,
			  }
			: {}),
	};

	return { sliderSettings: mergedSliderSettings };
};
